import { Add, DeleteOutline, Restore, Undo, WarningAmber } from "@mui/icons-material";
import { Checkbox, IconButton, Tooltip } from "@mui/material";
import { FC, Fragment, memo } from "react";
import RiskCellEditor from "./RiskCellEditor";
import RiskLinkOption from "./RiskLinkOption";
import { ASSESSMENT_TAB, LEFT_COLUMNS, LINE_SELECT_WIDTH, RISK, RiskColumn, SELECT_WIDTH, SOURCE_LINE_WIDTH, TAB_COLUMNS, tabWidth } from "./riskColumns";
import {
	CheckState, EditorLine, EditorRow, LINK_SOURCE_COMPANY, LinkIndex, RiskGridHandlers, caLabel, caLineWarning, columnLinkOptions, domainOptions,
	isRowChanged, isUtilizedEpi, linkOptionLabel, linkOptionSearchText, outsideLinkValues, utilizedEpiValue, withCurrentValues
} from "./riskEditorState";

interface Props {
	row: EditorRow;
	tab: string;
	readOnly: boolean;
	checkState: CheckState;
	selectedLines: string;
	editing: string;
	handlers: RiskGridHandlers;
	linkIndex: LinkIndex;
	options: Record<string, string[]>;
}

const RiskGridRow: FC<Props> = ({ row, tab, readOnly, checkState, selectedLines, editing, handlers, linkIndex, options }) => {
	const subTab = tab !== ASSESSMENT_TAB;
	const lines = subTab ? row.tabs[tab] || [] : [];
	const selected = new Set(selectedLines ? selectedLines.split('|') : []);
	const editableRow = !readOnly && !row.removed;
	const editableLines = editableRow;
	return (
		<div className={`risco-bloco${row.removed ? ' excluida' : ''}${row.added ? ' adicionada' : ''}`}>
			<div className="esquerda">
				<div className="celula selecao" style={{ width: SELECT_WIDTH }}>
					{editableLines && (
						<Checkbox
							size="small"
							checked={checkState === 'checked'}
							indeterminate={checkState === 'indeterminate'}
							onChange={() => handlers.onToggleRow(row)}
						/>
					)}
					{!readOnly && (
						<Tooltip title={row.added ? 'Remover risco adicionado' : row.removed ? 'Restaurar risco' : 'Excluir este risco do cargo'}>
							<IconButton size="small" onClick={() => handlers.onToggleRowRemoved(row)}>
								{row.removed ? <Restore fontSize="small"/> : <DeleteOutline fontSize="small"/>}
							</IconButton>
						</Tooltip>
					)}
					{isRowChanged(row) && !!handlers.onRevertRow && (
						<Tooltip title={row.added ? 'Desfazer inclusão deste risco' : 'Desfazer alterações deste risco'}>
							<IconButton size="small" onClick={() => handlers.onRevertRow?.(row)}>
								<Undo fontSize="small"/>
							</IconButton>
						</Tooltip>
					)}
				</div>
				<div
					className="celula identidade linha-origem"
					style={{ width: SOURCE_LINE_WIDTH }}
					title={row.added ? 'Risco adicionado, não está na planilha de origem' : `Linha ${row.sourceLine} da aba Avaliação da planilha de origem`}
				>
					{row.added ? 'Nova' : row.sourceLine}
				</div>
				{LEFT_COLUMNS.map(column => (
					<div key={column.id} className="celula identidade" style={{ width: column.width }} title={row.values[column.index]}>
						<span className="texto">{row.values[column.index]}</span>
					</div>
				))}
			</div>
			<div className="sub-linhas">
				{!subTab
					? <div className="sub-linha">
						{TAB_COLUMNS[tab].map(column => renderCell(column))}
					</div>
					: <Fragment>
						{lines.map(line => (
							<div key={line.id} className={`sub-linha${line.original === null ? ' adicionada' : ''}${line.removed ? ' excluida' : ''}`}>
								<div className="celula selecao" style={{ width: LINE_SELECT_WIDTH }}>
									{editableLines && !line.removed && (
										<Checkbox size="small" checked={selected.has(line.id)} onChange={() => handlers.onToggleLine(row, line.id)}/>
									)}
									{editableLines && (
										<Tooltip title={line.removed ? 'Restaurar linha' : 'Excluir linha'}>
											<IconButton
												size="small"
												onClick={() => line.removed ? handlers.onRestoreLine(row, line) : handlers.onRemoveLine(row, line)}
											>
												{line.removed ? <Restore fontSize="small"/> : <DeleteOutline fontSize="small"/>}
											</IconButton>
										</Tooltip>
									)}
								</div>
								{TAB_COLUMNS[tab].map(column => renderCell(column, line))}
							</div>
						))}
						{(editableLines || !lines.length) && (
							<div className={`sub-linha${lines.length ? ' nova-linha' : ''}`}>
								<div className="celula selecao" style={{ width: LINE_SELECT_WIDTH }}>
									{editableLines && (
										<Tooltip title="Adicionar linha nesta aba">
											<IconButton size="small" onClick={() => handlers.onAddLine(row)}>
												<Add fontSize="small"/>
											</IconButton>
										</Tooltip>
									)}
								</div>
								<div className="celula vazia" style={{ width: tabWidth(tab) - LINE_SELECT_WIDTH }}>
									{!lines.length && 'Nenhum registro'}
								</div>
							</div>
						)}
					</Fragment>
				}
			</div>
		</div>
	);

	function renderCell(column: RiskColumn, line?: EditorLine) {
		const value = (line ? line.values[column.index] : row.values[column.index]) || '';
		const original = line ? (line.original ? line.original[column.index] : null) : row.original[column.index];
		const changed = original !== null && value !== original;
		const cellKey = `${line ? line.id : ''}|${column.id}`;
		const outsideValues = outsideLinkValues(linkIndex, row.values[RISK], column, value);
		const caWarning = line && column.link === 'CA' ? caLineWarning(row, line, linkIndex) : null;
		const outsideWarning = !outsideValues.length
			? null
			: column.multiple
				? `Fora dos vínculos deste risco na empresa ${LINK_SOURCE_COMPANY}: ${outsideValues.join(', ')}`
				: `Valor fora dos vínculos deste risco na empresa ${LINK_SOURCE_COMPANY}`;
		const warning = caWarning || outsideWarning;
		const utilized = !!line && isUtilizedEpi(column);
		const editorValue = utilized && line ? utilizedEpiValue(row, line) : value;
		const display = column.link === 'CA' ? caLabel(linkIndex, value) : value;
		const editable = editableLines && !line?.removed;
		const isEditing = editing === cellKey;
		return (
			<div
				key={column.id}
				className={`celula${editable ? ' editavel' : ''}${changed ? ' alterada' : ''}`}
				style={{ width: column.width }}
				title={changed ? `Original: ${original || '(vazio)'}` : display}
				onClick={() => {
					if (editable && !isEditing) {
						handlers.onStartEdit({ rowKey: row.key, lineId: line?.id, columnId: column.id });
					}
				}}
			>
				{isEditing
					? <RiskCellEditor
						column={column}
						value={editorValue}
						options={column.link ? columnLinkOptions(linkIndex, row.values[RISK], column, editorValue) : withCurrentValues(column, domainOptions(options, column, row), value)}
						getLabel={column.link ? (option) => linkOptionLabel(linkIndex, column, option) : undefined}
						getSearchText={column.link ? (option) => linkOptionSearchText(linkIndex, column, option) : undefined}
						renderOptionContent={column.link ? (option) => <RiskLinkOption index={linkIndex} column={column} option={option}/> : undefined}
						emptyText={column.link ? 'Nenhum valor vinculado a este risco' : 'Sem opções'}
						onCommit={(next) => handlers.onCommitEdit({ rowKey: row.key, lineId: line?.id, columnId: column.id }, next)}
						onCancel={handlers.onCancelEdit}
					/>
					: <span className="texto">{display}</span>
				}
				{warning && !isEditing && (
					<Tooltip title={warning}>
						<span className="aviso-icone">
							<span title="">
								<WarningAmber className="aviso" fontSize="small"/>
							</span>
						</span>
					</Tooltip>
				)}
			</div>
		);
	}
};

export default memo(RiskGridRow);
